import { useCallback, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  isAllowedProductDrawingFile,
  PRODUCT_DRAWING_FILE_ACCEPT,
} from '@/v2/utils/productDrawingFileTypes'

type AddProductDrawingsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUploadFiles: (files: File[]) => Promise<void>
}

export function AddProductDrawingsDialog({
  open,
  onOpenChange,
  onUploadFiles,
}: AddProductDrawingsDialogProps) {
  const { t } = useTranslation('common')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleFiles = useCallback(
    async (files: File[]) => {
      const allowedFiles = files.filter((file) => isAllowedProductDrawingFile(file))
      if (allowedFiles.length === 0) {
        toast.error(t('v2.pages.products.detail.drawings.addDialog.allowedFileTypes'))
        return
      }

      setIsUploading(true)
      try {
        await onUploadFiles(allowedFiles)
        onOpenChange(false)
      } finally {
        setIsUploading(false)
      }
    },
    [onOpenChange, onUploadFiles, t]
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files
      if (fileList != null && fileList.length > 0) {
        void handleFiles(Array.from(fileList))
      }
      event.target.value = ''
    },
    [handleFiles]
  )

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      const fileList = event.dataTransfer.files
      if (fileList.length > 0) {
        void handleFiles(Array.from(fileList))
      }
    },
    [handleFiles]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t('v2.pages.products.detail.drawings.addDialog.title')}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {t('v2.pages.products.detail.drawings.addDialog.description')}
          </p>
        </DialogHeader>
        <div
          className={cn(
            'flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center',
            'border-muted-foreground/25 hover:border-muted-foreground/50',
            isUploading && 'pointer-events-none opacity-60'
          )}
          onDragOver={(event) => {
            event.preventDefault()
          }}
          onDrop={handleDrop}
        >
          <Upload className="mb-2 size-10 text-muted-foreground" />
          <p className="mb-3 text-sm text-muted-foreground">
            {t('v2.pages.products.detail.drawings.addDialog.dragDrop')}
          </p>
          <p className="mb-4 text-xs text-muted-foreground">
            {t('v2.pages.products.detail.drawings.addDialog.orClick')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={PRODUCT_DRAWING_FILE_ACCEPT}
            multiple
            className="sr-only"
            onChange={handleInputChange}
            disabled={isUploading}
          />
          <Button
            disabled={isUploading}
            variant="secondary"
            size="sm"
            onClick={() => {
              fileInputRef.current?.click()
            }}
          >
            {t('v2.pages.products.detail.drawings.addDialog.selectFiles')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
